// Three-card summary strip above the Insurance/Licence list. Surfaces
// In force / Expiring soon / Expired counts so admins see the active
// compliance gap at a glance.
//
// Superseded (renewed) handling is status-specific:
//   - In force: counts `current` AND `expiring` active coverage. An
//     expiring-but-superseded ("Renewed") policy is still in effect today, so
//     it STILL counts here — renewal doesn't remove today's coverage.
//   - Expiring soon: counts `expiring`, but EXCLUDES expiring-superseded —
//     once renewed it no longer needs a renewal prompt (and raises no dot).
//   - Expired: counts `expired`, but EXCLUDES expired-superseded — audit-only
//     lapsed data must not inflate the figure that drives action.
// The working set (the "X of Y records" denominator) excludes expired-
// superseded rows only; expiring-superseded rows remain in the active set.
// Future-start (`upcoming`) policies are contracted but not yet providing
// coverage, so they are excluded from In force.

import MetricCard from "../deliverable/MetricCard";
import type { Insurance, InsuranceStatus } from "../../../../types";
import { deriveInsuranceStatus } from "../../../../utils/deriveInsuranceStatus";
import { humaniseDays } from "../../../../utils/formatCoverage";

// `humaniseDays` is used for the Expiring-soon subtitle ("in 8 weeks");
// the Expired subtitle uses raw days ("expired Nd ago") because the absolute
// gap is what drives action — the day-count is the audit-relevant figure
// even when months have passed.

export interface InsuranceSummaryStripProps {
  insurances: Insurance[];
  /** Ids the renewal hook flagged as superseded (renewed). Expired-superseded
   *  are dropped from the Expired count + working set; expiring-superseded are
   *  dropped from Expiring soon but kept under In force (still active cover). */
  supersededIds: Set<string>;
  /** Anchor date for "in N weeks" / "expired N days ago" calculations.
   *  Tests inject a fixed Date; production passes Brisbane-now. */
  today: Date;
}

function dateToIso(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function isoToUtcMs(iso: string): number {
  const [y, m, d] = iso.split("-").map(Number);
  return Date.UTC(y, m - 1, d);
}

function daysBetween(fromIso: string, toIso: string): number {
  return Math.round((isoToUtcMs(toIso) - isoToUtcMs(fromIso)) / 86_400_000);
}

function resolveStatus(item: Insurance): InsuranceStatus | undefined {
  return (
    item.derivedStatus ??
    deriveInsuranceStatus(item.expiryDate, item.startDate, item.reminderDays)
  );
}

export default function InsuranceSummaryStrip({
  insurances,
  supersededIds,
  today,
}: InsuranceSummaryStripProps) {
  if (insurances.length === 0) return null;

  const todayIso = dateToIso(today);

  // Working set: drop archived rows and expired-superseded rows only.
  // Expiring-superseded ("Renewed") rows are still active cover, so they
  // stay in the set and count toward In force.
  const activeItems = insurances.filter((i) => {
    if (i.archivedAt) return false;
    return !(supersededIds.has(i.id) && resolveStatus(i) === "expired");
  });
  const inForceItems = activeItems.filter((i) => {
    const status = resolveStatus(i);
    // Active coverage = in effect today. Expiring-soon policies are still in
    // force (just within their renewal window) and must count here — including
    // expiring-superseded ones, which remain today's coverage until they lapse.
    return status === "current" || status === "expiring";
  });
  const expiringItems = activeItems.filter(
    // Renewed (superseded) expiring rows no longer need a renewal prompt.
    (i) => resolveStatus(i) === "expiring" && !supersededIds.has(i.id),
  );
  const expiredActive = activeItems.filter(
    (i) => resolveStatus(i) === "expired",
  );
  const futureCount = activeItems.filter(
    (i) => resolveStatus(i) === "upcoming",
  ).length;

  const inForceSubtitle =
    futureCount > 0
      ? `${inForceItems.length} of ${activeItems.length} records · ${futureCount} starting soon`
      : `${inForceItems.length} of ${activeItems.length} records`;

  const soonest = expiringItems
    .slice()
    .sort((a, b) => a.expiryDate.localeCompare(b.expiryDate))[0];
  const expiringSubtitle = soonest
    ? `${soonest.name} · in ${humaniseDays(
        Math.max(0, daysBetween(todayIso, soonest.expiryDate)),
      )}`
    : undefined;

  const worstExpired = expiredActive
    .slice()
    .sort((a, b) => a.expiryDate.localeCompare(b.expiryDate))[0];
  const worstExpiredDays = worstExpired
    ? Math.max(0, daysBetween(worstExpired.expiryDate, todayIso))
    : 0;
  const expiredSubtitle = worstExpired
    ? `${worstExpired.name} · expired ${worstExpiredDays}d ago`
    : "all coverage in force";

  return (
    <section
      data-testid="insurance-summary-strip"
      aria-label="Insurance summary"
      className="grid grid-cols-1 gap-3 md:grid-cols-3"
    >
      <MetricCard
        testId="metric-card-in-force"
        label="In force"
        valueTone="neutral"
        value={inForceItems.length}
        subtitle={inForceSubtitle}
      />
      <MetricCard
        testId="metric-card-expiring-soon"
        label="Expiring soon"
        valueTone="warning"
        value={expiringItems.length}
        subtitle={expiringSubtitle}
      />
      <MetricCard
        testId="metric-card-expired"
        label="Expired"
        valueTone="danger"
        value={expiredActive.length}
        subtitle={expiredSubtitle}
      />
    </section>
  );
}
